// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { Modal, StatusBanner, TextArea as FinTextArea } from '@loa/ui-components';
import { formatDateTime } from '@loa/theme';
import { resolveFlag } from '../../api/signatoryStatusApi';
import { SignatoryStatusBadge } from './SignatoryStatusBadge';

const MIN_NOTE_LENGTH = 5;
const MAX_NOTE_LENGTH = 500;

type ResolveFlagModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  profileId: string;
  policyId: string;
  signatoryId: string;
  policyNumber: string;
  clientName: string;
  flagReason?: string;
  flagCreatedAt?: string;
}>;

export function ResolveFlagModal({
  open,
  onClose,
  onSuccess,
  profileId,
  policyId,
  signatoryId,
  policyNumber,
  clientName,
  flagReason,
  flagCreatedAt,
}: ResolveFlagModalProps) {
  const [resolutionNote, setResolutionNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setResolutionNote('');
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const trimmedNote = resolutionNote.trim();
  const isConfirmDisabled =
    trimmedNote.length < MIN_NOTE_LENGTH ||
    trimmedNote.length > MAX_NOTE_LENGTH ||
    submitting;

  const handleConfirm = async () => {
    if (trimmedNote.length < MIN_NOTE_LENGTH) return;
    setSubmitting(true);
    setError(null);
    try {
      await resolveFlag(profileId, policyId, signatoryId, {
        resolutionNote: trimmedNote,
      });
      onSuccess();
      onClose();
    } catch (err) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 400) {
        setError('This signatory is not flagged or the flag cannot be resolved. Please refresh and try again.');
      } else if (axiosErr.response?.status === 404) {
        setError('Signatory not found. Please refresh and try again.');
      } else {
        setError('Failed to resolve flag. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_NOTE_LENGTH) {
      setResolutionNote(text);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="medium">
      <Modal.Header title="Resolve Signatory Flag" onClose={onClose} />
      <Modal.Content>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '4px' }}>
          <div style={{ fontSize: '0.9em' }}>
            <strong>Policy:</strong> {policyNumber}
          </div>
          <div style={{ fontSize: '0.9em' }}>
            <strong>Client:</strong> {clientName}
          </div>
          <div style={{ fontSize: '0.9em' }}>
            <strong>Current Status:</strong>{' '}
            <SignatoryStatusBadge status="FLAGGED" size="tiny" />
          </div>
          {flagReason && (
            <div style={{ fontSize: '0.9em' }}>
              <strong>Flag Reason:</strong> {flagReason}
            </div>
          )}
          {flagCreatedAt && (
            <div style={{ fontSize: '0.9em' }}>
              <strong>Flagged At:</strong> {formatDateTime(flagCreatedAt)}
            </div>
          )}
        </div>

        <StatusBanner
          variant="neutral"
          message={
            <>
              Resolving this flag will move the signatory back to <strong>Pending Review</strong> status.
              A resolution note is required for audit purposes.
            </>
          }
          visible
          position="inline"
          dismissible={false}
        />

        <div style={{ marginTop: '1rem' }}>
          <FinTextArea
            label={`Resolution Note (required, ${MIN_NOTE_LENGTH}-${MAX_NOTE_LENGTH} characters)`}
            placeholder="Describe how the flag was resolved..."
            value={resolutionNote}
            onChange={handleNoteChange}
            disabled={submitting}
            rows={3}
            aria-label="Resolution note"
            fluid
          />
          <div style={{ textAlign: 'right', fontSize: '0.85em', color: '#888', marginTop: '0.25rem' }} aria-live="polite">
            {trimmedNote.length}/{MAX_NOTE_LENGTH}
            {trimmedNote.length > 0 && trimmedNote.length < MIN_NOTE_LENGTH && (
              <span style={{ color: '#db2828' }}>
                {' '}(minimum {MIN_NOTE_LENGTH} characters)
              </span>
            )}
          </div>
        </div>

        {error && (
          <StatusBanner
            variant="error"
            message={error}
            visible
            position="inline"
            dismissible={false}
          />
        )}
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: submitting }}
        primaryAction={{ label: 'Resolve Flag', onClick: () => { void handleConfirm(); }, disabled: isConfirmDisabled, loading: submitting }}
      />
    </Modal>
  );
}
